import { AfterViewInit, Component, EventEmitter, Input, NgZone, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { IDistrictPolygon, IGoogleMapMarkerModel } from '@shared/common/common-types';
import GoogleMapConstants from '@shared/utils/google-map-constants';
import { boundsEqual } from '@shared/utils/google-map-utils';
import { IMapBoundsChange } from '@shared/utils/google-map-utils';

@Component({
    selector: 'app-google-map',
    templateUrl: './google-map.component.html',
    styleUrl: './google-map.component.less',
    imports: [GoogleMapsModule],
})
class GoogleMapComponent implements AfterViewInit, OnChanges {
    @Input() center: google.maps.LatLngLiteral = { lat: 42.69, lng: 23.31 };

    @Input() markers: IGoogleMapMarkerModel[] = [];

    @Input() options: google.maps.MapOptions = GoogleMapConstants.defaultMapOptions;

    @Input() mapId = GoogleMapConstants.mapId;

    @Input() polygons: google.maps.Polygon[] = [];

    @Input() polygonModels: IDistrictPolygon[] = [];

    @Input() zoom = 10;

    @Input() height: string = '780px';

    @Input() width: string = '100%';

    @Input() debounceTime = 300; // milliseconds

    @Output() boundsChanged = new EventEmitter<IMapBoundsChange>();

    @Output() markerClick: EventEmitter<IGoogleMapMarkerModel> = new EventEmitter<IGoogleMapMarkerModel>();

    @Output() polygonClick: EventEmitter<IDistrictPolygon> = new EventEmitter<IDistrictPolygon>();

    @Output() mapInitialized: EventEmitter<google.maps.Map> = new EventEmitter<google.maps.Map>();

    @ViewChild(GoogleMap) googleMapComponent!: GoogleMap;

    map!: google.maps.Map;

    private lastEmittedBounds: google.maps.LatLngBounds | null = null;

    private initialEmitDone = false;

    constructor(private ngZone: NgZone) {}

    ngAfterViewInit() {
        this.googleMapComponent.mapInitialized.subscribe((map) => {
            this.ngZone.run(() => this.onMapInitialized(map));
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.polygonModels && this.map) {
            this.attachPolygonListeners();
        }
    }

    onMapInitialized(map: google.maps.Map) {
        this.map = map;
        this.mapInitialized.emit(this.map);

        this.map.addListener('idle', () => {
            this.ngZone.runOutsideAngular(() => {
                const bounds = this.map.getBounds();
                const center = this.map.getCenter();
                const zoom = this.map.getZoom();
                if (bounds && !boundsEqual(bounds, this.lastEmittedBounds)) {
                    this.lastEmittedBounds = bounds;

                    const mapBoundsChange: IMapBoundsChange = { bounds, center, zoom };
                    this.ngZone.run(() => this.boundsChanged.emit(mapBoundsChange));
                }

                if (!this.initialEmitDone && bounds) {
                    this.initialEmitDone = true;
                }
            });
        });
    }

    onPolygonClick(polygon: IDistrictPolygon) {
        this.polygonClick.emit(polygon);
    }

    private attachPolygonListeners(): void {
        if (!this.map || !this.polygonModels?.length) {
            return;
        }

        this.polygonModels.forEach((polygonModel) => {
            polygonModel.polygon.setMap(this.map);
            google.maps.event.clearListeners(polygonModel.polygon, 'click');
            polygonModel.polygon.addListener('click', () => {
                this.ngZone.run(() => this.onPolygonClick(polygonModel));
            });
        });
    }
}

export default GoogleMapComponent;
